import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Wrapper from "../../helpers/Wrapper";
import { useSelector } from "react-redux";
import { CountryCode } from "../../../constants/countryCurrency";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";

am5.addLicense("AM5C258896422");

const SimpleTreeMap = ({
  chartId,
  chartClass,
  chartData,
  levelHandler,
  colorChangeParameter,
  chartLabels,
  positiveNegativeNeutral
}) => {
  if (positiveNegativeNeutral === undefined) {
    positiveNegativeNeutral = false
  }

  // USER DATA
  const userData = useSelector((state) => state.userData.user[0]);
  let userCountryCode = userData?.country_code ?? "IN"
  let country = {}
  CountryCode.map((data) => {
    if (data.countryCode === userCountryCode) {
      country = data
    }
  })

  useLayoutEffect(() => {
    var root = am5.Root.new(chartId);

    // to format numbers
    root.numberFormatter.setAll({
      numberFormat: "#.##a",
      bigNumberPrefixes: [
        { number: 1e3, suffix: "K" },
        { number: 1e6, suffix: "M" },
        { number: 1e9, suffix: "B" },
      ],
      smallNumberPrefixes: [],
    });

    root.setThemes([
      am5themes_Animated.new(root)
    ]);

    var data = {
      name: "Root",
      children: chartData
    };

    var container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: root.verticalLayout
      })
    );

    function createSeries(label, index) {
      var series = container.children.push(
        am5hierarchy.Treemap.new(root, {
          singleBranchOnly: false,
          downDepth: 0,
          upDepth: -1,
          initialDepth: 2,
          valueField: label.value,
          categoryField: label.name,
          childDataField: "children",
          nodePaddingOuter: 0,
          nodePaddingInner: 0,
          tooltipValue1: label.toolTipvalue1,
          tooltipValue1Label: label.toolTipvalue1Label,
          tooltipValue2: label.toolTipvalue2,
          tooltipValue2Label: label.toolTipvalue2Label,
          tooltipValue3: label.toolTipvalue3,
          tooltipValue3Label: label.toolTipvalue3Label,
        })
      );
      var tooltip = series.set(
        "tooltip", am5.Tooltip.new(root, {
          getFillFromSprite: false,
          getStrokeFromSprite: false,
          autoTextColor: false,
          pointerOrientation: "horizontal",
          position: "pointer",
          labelText: "[bold]{name}[/]\n",
        })
      );

      tooltip.get("background").setAll({
        fill: am5.color(0x213345),
        cornerRadius: 5,
      });

      tooltip.label.setAll({
        fill: am5.color(0xffffff),
        fontSize: 12,
        fontFamily: "Ubuntu",
        fontWeight: "bold"
      });

      tooltip.label.adapters.add("text", function (text, target) {
        // Display Sales Value first, then Sales Value Growth with bold font
        series.get("tooltipValue2") === undefined &&
          (
            text += "\n[bold]●[/]  [bold]" + series.get("tooltipValue1Label") + ":[/]" + " " + "[bold  width:100px]" + formatNumberForTooltip(target?.dataItem?.dataContext[series?.get("tooltipValue1")], series.get("tooltipValue1Label")) + "[/]"
          );

        series.get("tooltipValue2") !== undefined && series.get("tooltipValue3") !== undefined &&
          (
            text += "[]●[/]  [bold]" + series.get("tooltipValue2Label") + ":[/]" + " " + "[bold  width:100px]" + formatNumberForTooltip(target?.dataItem?.dataContext[series?.get("tooltipValue2")], series.get("tooltipValue2Label")) + "[/]" +
            "\n[]●[/]  [bold]" + series.get("tooltipValue1Label") + ":[/]" + " " + "[bold  width:100px]" + formatNumberForTooltip(target?.dataItem?.dataContext[series?.get("tooltipValue1")], series.get("tooltipValue1Label")) + "[/]" +
            "\n[]●[/]  [bold]" + series.get("tooltipValue3Label") + ":[/]" + " " + "[bold  width:100px]" + formatNumberForTooltip(target?.dataItem?.dataContext[series?.get("tooltipValue3")], series.get("tooltipValue3Label")) + "[/]"
          );

        series.get("tooltipValue2") !== undefined && series.get("tooltipValue3") === undefined &&
          (
            text += "[]●[/] [bold]" + series.get("tooltipValue2Label") + ":[/]" + " " + "[bold  width:100px]" + formatNumberForTooltip(target?.dataItem?.dataContext[series?.get("tooltipValue2")], series.get("tooltipValue2Label")) + "[/]" +
            "\n[]●[/] [bold]" + series.get("tooltipValue1Label") + ":[/]" + " " + "[bold  width:100px]" + formatNumberForTooltip(target?.dataItem?.dataContext[series?.get("tooltipValue1")], series.get("tooltipValue1Label")) + "[/]"
          );

        return text;
      });
      series.rectangles.template.events.on("click", function (ev) {
        levelHandler(ev.target.dataItem.dataContext);
      });

      // --------- FIX: use color from data if present ----------
      series.rectangles.template.adapters.add("fill", function (fill, target) {
        if (target.dataItem?.dataContext?.color) {
          return am5.color(target.dataItem.dataContext.color);
        }
        return fill;
      });
      // --------------------------------------------------------

      series.rectangles.template.states.create("hover", {
        fill: am5.color(0x4cc3fd),
        fillOpacity: 1
      });

      series.rectangles.template.setAll({
        cornerRadiusTL: 4,
        cornerRadiusTR: 4,
        cornerRadiusBL: 4,
        cornerRadiusBR: 4
      });

      series.labels.template.setAll({
        fontSize: 12,
        fontFamily: "Ubuntu",
        fill: "#ffffff",
        // fontWeight: "300",
        textAlign: "center",
        // text: "{category}\n{sum}[/]",
      });



      series.data.setAll([data]);
      series.set("selectedDataItem", series.dataItems[0]);
      series.appear(1000, 100);
    }

    chartLabels.map((label, index) => {
      createSeries(label, index);
    });

    return () => {
      root.dispose();
    };
  }, [chartData]);

  return (
    // <Wrapper>
    //   <div id={chartId} className={chartClass} style={{
    //     height: "340px",
    //     minHeight: "340px",
    //     background: "#fff",
    //     borderRadius: "12px"
    //   }}></div>
    // </Wrapper>
    <Wrapper>
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%"
      }}>
        <div id={chartId} className={chartClass} style={{
          height: "340px",
          minHeight: "340px",
          background: "#fff",
          borderRadius: "12px",
          // margin: "16px",
          width: "97%"
        }}></div>
        {/* Legend here */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 24,
          marginBottom: 24,
          gap: 32,
          flexWrap: 'wrap'
        }}>
          <span style={{ color: "#213345", fontSize: 12, marginRight: 24 }}>
            Area = Market Share by Sales Value %
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              display: "inline-block",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              background: '#06a57d',
              marginRight: 3
            }} />
            <span style={{ color: "#213345", fontSize: 12 }}>
              Sales Value % Growth
            </span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              display: "inline-block",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              background: '#b51c2a',
              marginRight: 3
            }} />
            <span style={{ color: "#213345", fontSize: 12 }}>
              Sales Value % Degrowth
            </span>
          </span>
        </div>
      </div>
    </Wrapper>
  );
};

SimpleTreeMap.propTypes = {
  chartId: PropTypes.string,
  chartClass: PropTypes.string,
  chartData: PropTypes.array,
  levelHandler: PropTypes.func,
  colorChangeParameter: PropTypes.string,
  chartLabels: PropTypes.array,
  positiveNegativeNeutral: PropTypes.bool
};

export default SimpleTreeMap;